<template>
  <div class="chart-container">
    <!-- Show Chart if loaded -->
    <Pie v-if="simsLoaded" :data="chartData" :options="chartOptions" />

    <!-- Show Spinner only while loading -->
    <div v-else-if="loading" class="spinner-border m-5" role="status" style="width: 10rem; height: 10rem;">
      <span class="visually-hidden">Loading SIMs...</span>
    </div>

    <!-- Show error message if it failed and stopped loading -->
    <div v-else-if="hasError" class="text-danger m-5">
      <p>Failed to load SIM data</p>
      <button class="btn btn-sm btn-outline-secondary" @click="loadSims">Retry</button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Colors, Title } from 'chart.js';
import { Pie } from 'vue-chartjs';
// import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint } from '@/utils/config';
import { apiFetch } from '@/utils/api';

ChartJS.register(ArcElement, Tooltip, Legend, Colors, Title);

const auth = useAuthStore();
const schemaStore = useSchemaStore();
// const { schema, isLoading } = storeToRefs(schemaStore);

const loading = ref(false); // Track if request is in flight
const simsLoaded = ref(false);
const hasError = ref(false); // Track if request failed

const simCount = ref(0);
const chartData = reactive({ labels: [], datasets: [] });
const chartOptions = reactive({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: { display: false, text: 'SIM Inventory' },
    legend: { display: true, position: 'right', align: 'center' },
  }
});

const loadSims = async () => {
  loading.value = true;
  simsLoaded.value = false;
  hasError.value = false;
  const queryParams = [];
  const query = queryParams.length > 0 ? ('?' + queryParams.join('&')) : '';
  console.info('Loading SIM data for status summary...');
  try {
    const { items: sims, ...meta } = await apiFetch(`${ApiEndpoint.SIMS}${query}`);
    if (!Array.isArray(sims)) throw new Error('Invalid SIM list');
    
    simCount.value = sims.length;
    chartOptions.plugins.title.text = `Total SIMs: ${simCount.value}`;
    chartOptions.plugins.title.display = true;

    const labels = schemaStore.getEnumValues('SimStateType');
    const bgColors = [];
    const simSums = [];

    const colors = {
      'ISSUED': 'LightGray',
      'ACTIVATED': 'MediumSeaGreen',
      'SUSPENDED': 'Orange',
      'DEACTIVATED': 'Tomato'
    };

    labels.forEach(label => {
      simSums.push(sims.filter(sim => sim.state === label).length);
      bgColors.push(colors[label] || 'Black');
    });

    chartData.labels = labels;
    chartData.datasets = [{ backgroundColor: bgColors, data: simSums }];

    simsLoaded.value = true;
  } catch (err) {
    console.error(`Failed to load SIM data: ${err.message}`);
    hasError.value = true;
  } finally {
    // This runs whether the try blocks succeeds OR catches an error
    loading.value = false;
  }
};

onMounted(() => {
  loadSims();
});
</script>
